import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AUTOMATIONS_ACCESS_SERVICE, AccessService } from '@galaxy/automata/shared';
import { TranslateModule } from '@ngx-translate/core';
import { PublishedState, TaskDefinition } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyCheckboxModule } from '@vendasta/galaxy/checkbox';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { Observable } from 'rxjs';
import { DISABLED_ICON_COLOR } from '../../../../../common/constants';
import { Category } from '../../../../../data-services/category';
import { EndTaskDefinitionId } from '../../../../component-loader/constants';
import { isCustomIcon } from './../../../../../common/custom-icons';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';

@Component({
  selector: 'automata-task-category-list',
  templateUrl: './task-category-list.component.html',
  styleUrls: ['./task-category-list.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyInputModule,
    GalaxyWrapModule,
    GalaxyCheckboxModule,
    GalaxyTooltipModule,
    MatTooltipModule,
  ],
})
export class TaskCategoryListComponent implements OnInit, OnChanges {
  /** When true, disabled items are shown with a lock icon, otherwise they are hidden. */
  @Input() showDisabledItems = false;
  @Input() categories: Category[] = [];
  @Output() taskClicked: EventEmitter<TaskDefinition> = new EventEmitter<TaskDefinition>();
  @Input() useTriggerInvalidMessageInsteadOfActionInvalidMessage = false;

  /** The categories currently shown in the component. May be updated at any time. */
  filteredCategories: Category[] = [];
  DISABLED_ICON_COLOR = DISABLED_ICON_COLOR;
  DRAFT: PublishedState = PublishedState.PUBLISHED_STATE_DRAFT;
  // task definition that has been released to general but usage may result in unexpected behaviour/UI
  earlyAccessTaskDefinitionId = EndTaskDefinitionId;

  // "unused", but required to display the AI Icon
  aiIconService = inject(GalaxyAiIconService);

  constructor(@Inject(AUTOMATIONS_ACCESS_SERVICE) private accessService: AccessService) {}

  ngOnChanges(): void {
    this.filterCategories();
  }

  ngOnInit(): void {
    this.filterCategories();
  }

  /**
   * Takes the original category list and applies filters to ensure only those the user wants to see are displayed.
   * @private
   */
  private filterCategories(): void {
    // Reset what's displayed by copying the master list.
    this.filteredCategories = [];
    this.categories.forEach((category) => {
      this.filteredCategories.push({
        label: category.label,
        description: category.description,
        category: category.category,
        contents: [...category.contents],
      });
    });

    // Then, clean out unselectable items and categories that have no selectable items (if so desired).
    if (!this.showDisabledItems) {
      // Only retain selectable items
      this.filteredCategories.forEach(
        (category) =>
          (category.contents = category.contents?.filter(
            (item) => item.metadata.valid && item.metadata.hasAccessToRun,
          )),
      );

      // Remove entirely empty categories
      this.filteredCategories = this.filteredCategories.filter((category) => category.contents?.length > 0);
    }
  }

  taskWasClicked(taskDefinition: TaskDefinition): void {
    this.taskClicked.emit(taskDefinition);
  }

  isCustomIcon(name: string): boolean {
    return isCustomIcon(name);
  }

  hasAccessToFeatures(features: string[]): Observable<boolean> {
    return this.accessService.hasAccessToFeatures(features);
  }
}
